import React, { useEffect, useRef, useState } from 'react';

interface RemoteAudioPlayerProps {
  stream: MediaStream;
  peerId: string;
  onAudioLevel?: (level: number) => void;
}

const RemoteAudioPlayer: React.FC<RemoteAudioPlayerProps> = ({ 
  stream, 
  peerId,
  onAudioLevel 
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>();
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement || !stream) return;

    console.log('[RemoteAudioPlayer] Setting up audio for peer:', peerId);

    // Set the stream
    audioElement.srcObject = stream;

    // Play audio
    const playAudio = async () => {
      try {
        await audioElement.play();
        setIsPlaying(true);
        console.log('[RemoteAudioPlayer] Playing audio for peer:', peerId);
      } catch (err) {
        console.error('[RemoteAudioPlayer] Error playing audio:', err);
      }
    };

    playAudio();

    // Set up audio analysis if callback provided
    if (onAudioLevel) {
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;

      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

      const updateAudioLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          const normalized = Math.min(1, average / 128);
          onAudioLevel(normalized);
        }
        animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };

      updateAudioLevel();
    }

    return () => {
      console.log('[RemoteAudioPlayer] Cleanup for peer:', peerId);
      if (audioElement) {
        audioElement.pause();
        audioElement.srcObject = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stream, peerId, onAudioLevel]);

  return (
    <audio 
      ref={audioRef} 
      autoPlay 
      playsInline
      style={{ display: 'none' }}
    />
  );
};

export default RemoteAudioPlayer;
